import { Add, ArrowPointDown, ArrowPointUp, Delete, External, List } from "assets/icons/Common";
import { Col, IconButtonInverted, Row } from "components/primitives";
import { Identifier, XYCoord } from "dnd-core";
import React, { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";

import { ScheduleItemData } from "./interfaces";
import {
  DndBox,
  EmptySpace,
  ScheduleItem,
  ScheduleItemAdd,
  ScheduleItemButton,
  ScheduleItemDelete,
  ScheduleItemImage,
  ScheduleItemInner,
  ScheduleItemLink,
} from "./scheduleSetting.styles";

interface CurrentScheduleSettingItemProps {
  item: ScheduleItemData;
  onUpdateOrder: (dragIndex: number, hoverIndex: number) => void;
  onClick: () => void;
  index: number;
  dataLength: number;
  onDelete?: () => void;
  onAdd?: () => void;
}

const ITEM_NAME = "scheduleItem";
interface DragItem {
  index: number;
  id: string;
  type: string;
}

interface CollectedProps {
  handlerId: Identifier | null;
}

export const CurrentScheduleSettingItem = ({
  item,
  onUpdateOrder,
  onClick,
  index,
  onDelete,
  dataLength,
  onAdd,
}: CurrentScheduleSettingItemProps) => {
  const dndRef = useRef<HTMLDivElement>(null);
  const [{ handlerId }, drop] = useDrop<DragItem, void, CollectedProps>({
    accept: ITEM_NAME,
    collect(monitor): CollectedProps {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!dndRef.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = dndRef.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      onUpdateOrder(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: ITEM_NAME,
    item: () => {
      return { index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const handlePreventDnd = (
    event: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleMoveUp = () => {
    if (!index) {
      return;
    }

    onUpdateOrder(index, index - 1);
  };

  const handleMoveDown = () => {
    if (index + 1 === dataLength) {
      return;
    }

    onUpdateOrder(index, index + 1);
  };

  const opacity = { opacity: isDragging ? 0 : 1 };
  drag(drop(dndRef));

  return (
    <div>
      <ScheduleItem ref={dndRef} style={opacity} data-handler-id={handlerId}>
        <ScheduleItemInner>
          <DndBox color={item.color}>
            <IconButtonInverted
              Icon={ArrowPointUp}
              withShadow={false}
              onClick={handleMoveUp}
              disabled={!index}
            />

            <IconButtonInverted Icon={List} withShadow={false} />

            <IconButtonInverted
              Icon={ArrowPointDown}
              withShadow={false}
              onClick={handleMoveDown}
              disabled={index + 1 === dataLength}
            />
          </DndBox>

          <ScheduleItemButton onMouseDown={handlePreventDnd} color={item.color} onClick={onClick}>
            <Row>
              <ScheduleItemImage>{Boolean(item.image) && item.image}</ScheduleItemImage>
              <Col>
                <p
                  style={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    maxWidth: "100px",
                  }}
                >
                  <b>{item.activity}</b>
                </p>
                <small>
                  {item.time &&
                    new Date(item.time).toLocaleTimeString("en-us", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                </small>
              </Col>
              <ScheduleItemLink>{Boolean(item.link) && <External />}</ScheduleItemLink>
            </Row>
          </ScheduleItemButton>
        </ScheduleItemInner>
        {onDelete && (
          <ScheduleItemDelete
            onMouseDown={handlePreventDnd}
            withShadow={false}
            accentColor="var(--danger)"
            Icon={Delete}
            onClick={onDelete}
            title={"Remove activity"}
          />
        )}
        {onAdd && (
          <ScheduleItemAdd
            onMouseDown={handlePreventDnd}
            withShadow={false}
            accentColor="var(--primary)"
            Icon={Add}
            onClick={onAdd}
            title={"Assign activity"}
          />
        )}
      </ScheduleItem>
    </div>
  );
};
